import assert from 'assert';

import type { CurrencyFilterMeta, FilterMeta, FilterType } from '../types/filter-meta';
import type { FilterOption } from '../types/filter-option';
import type {
  CheckboxFilterValue,
  DateFilterValue,
  DateTimeFilterValue,
  FilterValue,
  NumberFilterValue,
  SelectFilterValue,
  TextFilterValue,
  TimeFilterValue,
} from '../types/filter-values';
import {
  isCheckboxFilterValue,
  isDateFilterValue,
  isDateTimeFilterValue,
  isNumberFilterValue,
  isSelectFilterValue,
  isTextFilterValue,
  isTimeFilterValue,
} from './filter-value-guards';

/**
 * Formats a filter value for display in a chip.
 */
export function formatFilterValue(
  filterType: FilterType,
  filterMeta: FilterMeta,
  options: FilterOption[],
  value: FilterValue,
): string {
  switch (filterType) {
    case 'select':
    case 'tag':
      assert(isSelectFilterValue(value), `Filter value does not match filter type "${filterType}"`);
      return formatSelectValue(options, value);
    case 'text':
      assert(isTextFilterValue(value), `Filter value does not match filter type "${filterType}"`);
      return formatTextValue(value);
    case 'number':
      assert(isNumberFilterValue(value), `Filter value does not match filter type "${filterType}"`);
      return formatNumberValue(value);
    case 'currency':
      assert(isNumberFilterValue(value), `Filter value does not match filter type "${filterType}"`);
      return formatCurrencyValue(value, (filterMeta as CurrencyFilterMeta).currencyCode);
    case 'date':
      assert(isDateFilterValue(value), `Filter value does not match filter type "${filterType}"`);
      return formatDateValue(value);
    case 'time':
      assert(isTimeFilterValue(value), `Filter value does not match filter type "${filterType}"`);
      return formatTimeValue(value);
    case 'datetime':
      assert(isDateTimeFilterValue(value), `Filter value does not match filter type "${filterType}"`);
      return formatDateTimeValue(value);
    case 'checkbox':
      assert(isCheckboxFilterValue(value), `Filter value does not match filter type "${filterType}"`);
      return formatCheckboxValue(value);
    default:
      return '';
  }
}

function formatSelectValue(options: FilterOption[], value: SelectFilterValue): string {
  if (value.operator === 'isEmpty') {
    return 'Empty';
  }

  const labels = value.values.map((v) => options.find((opt) => opt.value === v)?.label ?? String(v)).slice(0, 3);

  const display = labels.join(', ');
  const remaining = value.values.length - 3;

  if (remaining > 0) {
    const prefix = value.operator === 'noneOf' ? 'Not: ' : '';
    return `${prefix}${display} +${remaining}`;
  }

  if (value.operator === 'noneOf') {
    return `Not: ${display}`;
  }

  return display;
}

function formatTextValue(value: TextFilterValue): string {
  if (value.operator === 'isEmpty') {
    return 'Empty';
  }

  return `"${value.value}"`;
}

function formatNumberValue(value: NumberFilterValue): string {
  if (value.operator === 'isEmpty') {
    return 'Empty';
  }

  if (value.operator === 'between') {
    return `${value.min} – ${value.max}`;
  }

  const symbols: Record<string, string> = {
    eq: '',
    gt: '> ',
    gte: '≥ ',
    lt: '< ',
    lte: '≤ ',
  };

  return `${symbols[value.operator] ?? ''}${value.value}`;
}

function formatCurrencyValue(value: NumberFilterValue, currencyCode = 'USD'): string {
  if (value.operator === 'isEmpty') {
    return 'Empty';
  }

  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

  if (value.operator === 'between') {
    return `${formatter.format(value.min)} – ${formatter.format(value.max)}`;
  }

  const symbols: Record<string, string> = {
    eq: '',
    gt: '> ',
    gte: '≥ ',
    lt: '< ',
    lte: '≤ ',
  };

  return `${symbols[value.operator] ?? ''}${formatter.format(value.value)}`;
}

function formatDateValue(value: DateFilterValue): string {
  if (value.operator === 'isEmpty') {
    return 'Empty';
  }

  function formatDate(iso: string): string {
    const date = new Date(iso);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  if (value.operator === 'between') {
    return `${formatDate(value.start)} → ${formatDate(value.end)}`;
  }

  const prefixes: Record<string, string> = {
    eq: '',
    before: 'Before ',
    after: 'After ',
  };

  return `${prefixes[value.operator] ?? ''}${formatDate(value.value)}`;
}

function formatTimeValue(value: TimeFilterValue): string {
  if (value.operator === 'isEmpty') {
    return 'Empty';
  }

  if (value.operator === 'between') {
    return `${value.start} – ${value.end}`;
  }

  const prefixes: Record<string, string> = {
    eq: '',
    before: 'Before ',
    after: 'After ',
  };

  return `${prefixes[value.operator] ?? ''}${value.value}`;
}

function formatDateTimeValue(value: DateTimeFilterValue): string {
  if (value.operator === 'isEmpty') {
    return 'Empty';
  }

  function formatDateTime(iso: string): string {
    const date = new Date(iso);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  }

  if (value.operator === 'between') {
    return `${formatDateTime(value.start)} → ${formatDateTime(value.end)}`;
  }

  const prefixes: Record<string, string> = {
    eq: '',
    before: 'Before ',
    after: 'After ',
  };

  return `${prefixes[value.operator] ?? ''}${formatDateTime(value.value)}`;
}

function formatCheckboxValue(value: CheckboxFilterValue): string {
  const label = value.value ? 'Yes' : 'No';
  return value.operator === 'isNot' ? `Not ${label}` : label;
}
